// sketchSlice.js
import { createSlice } from "@reduxjs/toolkit";
import { setActiveBookId } from "./stepSlice";

const savedActiveBookId = () => {
  try {
    return localStorage.getItem("activeBookId") || null;
  } catch {
    return null;
  }
};

const initialState = {
  images: [],
  coverImages: [],
  batchId: null,
  allCompleted: false,
  isReusingBatch: false,
  // The book these photos belong to. Opening another book empties the lists, so
  // one book's cover or pages can never show up (or be sent to drawing) in another.
  forBookId: savedActiveBookId(),
};

const sketchSlice = createSlice({
  name: "sketch",
  initialState,
  reducers: {
    setCoverImages: (state, action) => {
      state.coverImages = Array.isArray(action.payload) ? action.payload : [];
    },
    updateCoverImage: (state, action) => {
      const { index, id, data } = action.payload;

      // Prefer index if available, else find by id
      const imageIndex =
        typeof index === "number"
          ? index
          : state.coverImages.findIndex((img) => img.id === id);

      if (imageIndex !== -1) {
        state.coverImages[imageIndex] = {
          ...state.coverImages[imageIndex],
          ...data,
        };
      }
    },
    replaceCoverImage: (state, action) => {
      state.coverImages = [action.payload]
    },
    deleteCoverImage: (state, action) => {
      const idToDelete = action.payload;
      state.coverImages = state.coverImages.filter(
        (img) => !!img._id ? (img._id !== idToDelete) : img.id !== idToDelete
      );
    },

    setImages: (state, action) => {
      const images = Array.isArray(action.payload) ? action.payload : [];
      // Dedup safety net: DB images come first in merged arrays, so first-wins
      const seen = new Set();
      state.images = images.filter((img) => {
        // Dedup by publicId (shared between pending and DB images)
        const publicId = img.publicId || img.AIinfo?.public_id;
        if (publicId) {
          if (seen.has(`pub:${publicId}`)) return false;
          seen.add(`pub:${publicId}`);
        }
        // Dedup by _id for DB images
        if (img._id) {
          if (seen.has(`db:${img._id}`)) return false;
          seen.add(`db:${img._id}`);
        }
        return true;
      });
    },
    addImage: (state, action) => {
      state.images = [...state.images, action.payload]
    },
    updateImage: (state, action) => {
      const { index, data } = action.payload;
      state.images[index] = { ...state.images[index], ...data };
    },
    replaceImage: (state, action) => {
      const index = state.images.findIndex(img => (
        img.id === action.payload.prevId || 
        img._id === action.payload.prevId
      ))
      if (index >= 0) {
        state.images[index] = {...action.payload, croppedSketchUrl:null}
      } else {
        // add the image if not found
        state.images = [...state.images, {...action.payload, croppedSketchUrl:null}]
      }
    },
    deleteImage: (state, action) => {
      const idToDelete = action.payload;
      state.images = state.images.filter((img) => (
        !!img._id ? (img._id !== idToDelete) : (img.id !== idToDelete)
      ));
    },
    setBatchId: (state, action) => {
      state.batchId = action.payload;
    },
    setIsReusingBatch: (state, action) => {
      state.isReusingBatch = action.payload;
    },
    setAllCompleted: (state, action) => {
      state.allCompleted = action.payload;
    },
    clearSketch: (state) => ({ ...initialState, forBookId: state.forBookId }),
  },
  extraReducers: (builder) => {
    builder.addCase(setActiveBookId, (state, action) => {
      const bookId = action.payload?.bookId || null;
      if (bookId === state.forBookId) return;
      state.images = [];
      state.coverImages = [];
      state.forBookId = bookId;
    });
  },
});

export const {
  setCoverImages,
  updateCoverImage,
  deleteCoverImage,
  setImages,
  addImage,
  updateImage,
  replaceImage,
  replaceCoverImage,
  deleteImage,
  setBatchId,
  setIsReusingBatch,
  setAllCompleted,
  clearSketch,
} = sketchSlice.actions;

export default sketchSlice.reducer;
